import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Stop as StopIcon } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useGlobalValue } from "../../GlobalContext";
import FullPageProgress from "../FullPageProgress";
import { ToastManager } from "../Toast/ToastManager";
import AIChatEditor from "./AIChatEditor";
import AIChatMessages from "./AIChatMessages";
import AIChatTabs from "./AIChatTabs";
import { SESSION_AUTH } from "../../common/auth";
import { AI_SERVICES, CHAT_SERVICES } from "../../services";
import {
  AI_MODELS_LIST,
  GET_PUBLIC_SESSION,
  SET_PUBLIC_SESSION,
} from "../../common/constants";
import "./style.scss";
let scrollAllow = true;
const AIChat = () => {
  const { listId } = useParams();
  const { isUserSession, LOGOUT_USER_SESSION } = SESSION_AUTH();
  const { seIsLoginRequest } = useGlobalValue();
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState("auto");
  const messagesRef = useRef(null);
  const [userData, setUserData] = useState("");
  const [topicId, setTopicId] = useState("");
  const [messageList, setMessageList] = useState([]);
  const [model, setModel] = useState(() => {
    let m = GET_PUBLIC_SESSION("EU_AI_MODEL");
    m = AI_MODELS_LIST?.[m] ? m : "claude-4-sonnet";
    return m;
  });

  const [dataFetched, setDataFetched] = useState(false);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [scroll, setScroll] = useState(0);
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState(null);

  useEffect(() => {
    if (isUserSession) {
      setTopicId(listId || "");
      return setUserData(isUserSession);
    } else {
      if (listId) {
        return navigate("/ai/chat");
      }
      return setUserData("");
    }
  }, [isUserSession, navigate, listId]);

  useEffect(() => {
    if (model) {
      SET_PUBLIC_SESSION("EU_AI_MODEL", model);
    }
  }, [model]);

  useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const Q_AI = params.get("q_ai");

    if (Q_AI) {
      setQuery(Q_AI);
      sendQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!userData || !topicId || dataFetched) return;
    setDataFetched(true);
    setLoading(true);
    CHAT_SERVICES.getChatList({
      userId: userData.userId,
      org: userData?.org?.id,
      id: topicId,
    })
      .then(data => {
        if (data.code === 200) {
          if (data?.data) {
            setMessageList(data?.data);
            setScroll(old => old + 1);
          }
        } else if (data.code === 600) {
          LOGOUT_USER_SESSION();
        } else {
          ToastManager.addToast({ title: data.message });
          navigate("/ai/chat");
        }
      })
      .catch(error => {
        ToastManager.addToast({ title: error.message });
        navigate("/ai/chat");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [topicId, userData, dataFetched, navigate, LOGOUT_USER_SESSION]);

  useEffect(() => {
    if (userData && query) {
      sendQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const topOffset = containerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const calculatedHeight = windowHeight - topOffset - 16;
        setContainerHeight(calculatedHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight); // Recalculate on resize

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  useEffect(() => {
    if (messagesRef.current && scroll) {
      messagesRef.current.scrollTo({
        top: (messagesRef.current.scrollHeight || 0) + 5000,
        behavior: "smooth",
      });
    }
  }, [scroll]);

  const sendQuery = async text => {
    if (!isUserSession) {
      return handleLogin();
    }
    let q = text ? text.trim() : query.trim();
    if (!q || sending) return;

    setSending(true);
    setQuery("");
    setMessageList(old => [...old, { query: q }]);

    const newController = new AbortController();
    const signal = newController.signal;
    setController(newController);
    setScroll(old => old + 1);
    const history = promptHistory();
    try {
      const { reader, decoder } = await AI_SERVICES.chat(
        {
          query: q,
          model: model,
          history: history,
        },
        signal
      );

      if (!reader || !decoder) {
        throw new Error("No stream found in response");
      }

      let result = "";
      let done = false;

      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;
        result += decoder.decode(value, { stream: true });

        // eslint-disable-next-line no-loop-func
        setMessageList(old => {
          const updatedList = [...old];
          updatedList[updatedList.length - 1]["response"] = result;
          return updatedList;
        });
        if (scrollAllow) {
          setScroll(old => old + 1);
        }
        if (done) {
          updateConversation(q, result);
        }
      }
    } catch (err) {
      setController(null);
    } finally {
      setSending(false);
    }
  };

  const promptHistory = () => {
    const conH = [];
    messageList.forEach(element => {
      if (element?.query) {
        conH.push(element?.query);
      }
    });
    return conH;
  };
  const handleScroll = () => {
    if (messagesRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesRef.current;
      if (scrollTop + clientHeight + 30 >= scrollHeight) {
        scrollAllow = true;
      } else {
        scrollAllow = false;
      }
    }
  };

  const handleLogin = () => {
    seIsLoginRequest(true);
  };

  const stopGenerating = () => {
    if (controller) {
      controller.abort();
    }
  };
  const updateConversation = (q, a) => {
    let postdata = {
      query: q,
      response: a,
      topicId: topicId,
      userId: userData?.userId,
      org: userData?.org?.id,
    };

    CHAT_SERVICES.chatUpdate(postdata)
      .then(res => {
        if (res.code === 200) {
          if (res?.data?.topicId) {
            navigate("/ai/chat/" + res?.data?.topicId);
          }
        } else if (res.code === 600) {
          LOGOUT_USER_SESSION();
        } else {
          ToastManager.addToast({ title: res.message });
        }
      })
      .catch(err => {
        ToastManager.addToast({ title: err.message });
      });
  };

  return (
    <div className="events6ix-layout-page-middle">
      <AIChatTabs />
      <div
        className="events-page-aichat-content"
        ref={containerRef}
        style={{ height: containerHeight }}
      >
        {loading ? <FullPageProgress fixed /> : null}
        <AIChatMessages
          messageList={messageList}
          userData={userData}
          messagesRef={messagesRef}
          handleScroll={handleScroll}
          sendQuery={sendQuery}
          sending={sending}
          handleLogin={handleLogin}
        />
        <div className="events-page-aichat-content-bottom">
          {sending && (
            <Button
              size="small"
              variant="outlined"
              className="events6ix-btn  events6ix-btn-primary events-page-aichat-content-bottom-indicator"
              onClick={stopGenerating}
              startIcon={<StopIcon />}
            >
              Stop generating
            </Button>
          )}
          <AIChatEditor
            model={model}
            setModel={setModel}
            sending={sending}
            sendQuery={sendQuery}
            query={query}
            setQuery={setQuery}
          />
          <div className="events-page-aichat-content-bottom-text">
            Explore 50,000+ companies across 90 exchanges
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChat;
